import React, { useEffect, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { ChangeRequest, ChangeRequestStatus, Project } from '../../../types';
import {
  getProjectChangeRequests, submitChangeRequest, updateChangeRequest,
} from '../../../services/changeRequestService';
import ModuleGuard from '../../../components/ModuleGuard';
import Badge from '../../../components/Badge';
import FilterPills from '../../../components/FilterPills';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton, BMSuccessButton, BM_LABEL, BM_TEXTAREA, BM_TITLE_INPUT,
} from '../../../components/BoardModal';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

interface Props { pca: IPublicClientApplication; projectId: string; project: Project; }

const STAT_LABEL: Record<number, string> = {
  100000000: 'Submitted', 100000001: 'Under Review', 100000002: 'Approved', 100000003: 'Rejected', 100000004: 'Withdrawn',
};
const STAT_BADGE: Record<number, { bg: string; fg: string }> = {
  100000000: { bg: '#D4EEF5', fg: '#0A5568' },
  100000001: { bg: '#FEF3C7', fg: '#92400E' },
  100000002: { bg: '#D1FAE5', fg: '#065F46' },
  100000003: { bg: '#FEE2E2', fg: '#991B1B' },
  100000004: { bg: '#F3F4F6', fg: '#5A6272' },
};

type StatusFilter = 'all' | 100000000 | 100000001 | 100000002 | 100000003 | 100000004;
const FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all',     label: 'All' },
  { value: 100000000, label: 'Submitted' },
  { value: 100000001, label: 'Under Review' },
  { value: 100000002, label: 'Approved' },
  { value: 100000003, label: 'Rejected' },
  { value: 100000004, label: 'Withdrawn' },
];

export default function ChangeRequestsTab(props: Props) {
  return (
    <ModuleGuard project={props.project} flag="dagrun_pm_module_changemanagement">
      <ChangeRequestsTabBody {...props} />
    </ModuleGuard>
  );
}

function ChangeRequestsTabBody({ pca, projectId }: Props) {
  const [items, setItems]       = useState<ChangeRequest[]>([]);
  const [filter, setFilter]     = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<ChangeRequest | null>(null);
  const [showNew, setShowNew]   = useState(false);
  const me = useCurrentUser(pca, projectId);

  const reload = () => getProjectChangeRequests(pca, projectId).then(setItems);
  useEffect(() => { reload(); }, [pca, projectId]); // eslint-disable-line

  const filtered = filter === 'all' ? items : items.filter(c => c.dagrun_pm_status === filter);

  return (
    <div style={{ padding: '20px 32px', fontFamily: 'var(--font-ui)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0F1117' }}>Change Requests</h3>
        <button type="button" onClick={() => setShowNew(true)} disabled={!me}
          style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: me ? 1 : 0.5 }}
          onMouseEnter={e => me && (e.currentTarget.style.background = '#0A5568')}
          onMouseLeave={e => (e.currentTarget.style.background = '#0E7490')}
        >+ Submit Change Request</button>
      </div>

      <div style={{ marginBottom: 14 }}>
        <FilterPills value={filter} options={FILTER_OPTIONS} onChange={setFilter} />
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#9198A8', fontSize: 14 }}>No change requests match this filter.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '1px solid #DDE0E6', textAlign: 'left' }}>
            <Th>Title</Th><Th>Status</Th><Th>Submitted By</Th><Th>Submitted On</Th>
          </tr></thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.dagrun_pm_changerequestid} onClick={() => setSelected(c)}
                style={{ borderBottom: '0.5px solid #EAECF0', cursor: 'pointer', transition: 'background 0.12s' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}>
                <Td><span style={{ color: '#0F1117', fontSize: 14 }}>{c.dagrun_pm_name}</span></Td>
                <Td><Badge {...STAT_BADGE[c.dagrun_pm_status]}>{STAT_LABEL[c.dagrun_pm_status]}</Badge></Td>
                <Td><span style={{ fontSize: 13, color: '#5A6272' }}>{c.dagrun_pm_submittedbyname ?? c.dagrun_pm_submittedby ?? '—'}</span></Td>
                <Td><span style={{ fontSize: 12, color: '#9198A8', fontFamily: 'var(--font-mono)' }}>{c.dagrun_pm_submittedon ? new Date(c.dagrun_pm_submittedon).toLocaleDateString() : '—'}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showNew && me && (
        <SubmitCRModal pca={pca} projectId={projectId} meOid={me.oid} meName={me.displayName}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); reload(); }}
        />
      )}
      {selected && me && (
        <CRDrawer pca={pca} cr={selected} isPM={me.isPM} meOid={me.oid} meName={me.displayName}
          onClose={() => { setSelected(null); reload(); }}
        />
      )}
    </div>
  );
}

const Th = ({ children }: { children: React.ReactNode }) => (
  <th style={{ padding: '8px 12px', fontWeight: 500, fontSize: 12, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</th>
);
const Td = ({ children }: { children: React.ReactNode }) => (
  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>{children}</td>
);

function SubmitCRModal({ pca, projectId, meOid, meName, onClose, onCreated }: { pca: IPublicClientApplication; projectId: string; meOid: string; meName: string; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [impact, setImpact] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || !description.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      await submitChangeRequest(pca, projectId, {
        name: name.trim(),
        description: description.trim(),
        impactassessment: impact.trim() || null,
        submittedby: meOid,
        submittedbyname: meName,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Submit failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Submit Change Request" onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !name.trim() || !description.trim()}>{saving ? 'Submitting…' : 'Submit'}</BMPrimaryButton>
      </>}
    >
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Title *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} style={BM_TITLE_INPUT} placeholder="Short, specific change request title" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Description *</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} style={BM_TEXTAREA} placeholder="What needs to change and why?" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Impact Assessment</label>
        <textarea value={impact} onChange={e => setImpact(e.target.value)} style={BM_TEXTAREA} placeholder="Optional — schedule, budget, scope impact." />
      </div>
      <div style={{ fontSize: 12, color: '#9198A8' }}>
        Submitted by <strong style={{ color: '#5A6272' }}>{meName}</strong> on {new Date().toLocaleDateString()}.
      </div>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

function CRDrawer({ pca, cr, isPM, meOid, meName, onClose }: { pca: IPublicClientApplication; cr: ChangeRequest; isPM: boolean; meOid: string; meName: string; onClose: () => void }) {
  const [draft, setDraft] = useState<ChangeRequest>(cr);
  const [approvalNotes, setApprovalNotes] = useState(cr.dagrun_pm_approvalnotes ?? '');
  const [savedFlash, setSavedFlash] = useState(false);
  const [acting, setActing] = useState<null | 'approve' | 'reject' | 'withdraw'>(null);
  const flashTimer = useRef<number | null>(null);

  useEffect(() => { setDraft(cr); setApprovalNotes(cr.dagrun_pm_approvalnotes ?? ''); }, [cr]);

  const flash = () => {
    setSavedFlash(true);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
  };

  const decide = async (status: number, label: 'approve' | 'reject' | 'withdraw') => {
    setActing(label);
    try {
      const fields: Partial<ChangeRequest> = {
        dagrun_pm_status: status,
        dagrun_pm_approvalnotes: approvalNotes || null,
        dagrun_pm_reviewedby: meOid,
        dagrun_pm_reviewedbyname: meName,
        dagrun_pm_reviewedon: new Date().toISOString(),
      };
      await updateChangeRequest(pca, draft.dagrun_pm_changerequestid, fields as Record<string, unknown>);
      setDraft(d => ({ ...d, ...fields }));
      flash();
    } finally { setActing(null); }
  };

  const isFinal = draft.dagrun_pm_status === ChangeRequestStatus.Approved
               || draft.dagrun_pm_status === ChangeRequestStatus.Rejected
               || draft.dagrun_pm_status === ChangeRequestStatus.Withdrawn;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.18)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 480,
        background: '#fff', borderLeft: '0.5px solid #EAECF0',
        boxShadow: '0 4px 24px rgba(15,17,23,0.12)', zIndex: 101,
        overflowY: 'auto', padding: '16px 24px 32px 24px',
        fontFamily: 'var(--font-ui)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#9198A8', opacity: savedFlash ? 1 : 0, transition: 'opacity 0.2s' }}>{savedFlash ? 'Saved ✓' : ''}</span>
          <button onClick={onClose} aria-label="Close" style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer', color: '#5A6272', fontSize: 18 }}>×</button>
        </div>

        <div style={{ fontSize: 18, fontWeight: 600, color: '#0F1117', marginBottom: 8 }}>{draft.dagrun_pm_name}</div>
        <div style={{ marginBottom: 16 }}>
          <Badge {...STAT_BADGE[draft.dagrun_pm_status]}>{STAT_LABEL[draft.dagrun_pm_status]}</Badge>
        </div>

        <Eyebrow>Description</Eyebrow>
        <Body>{draft.dagrun_pm_description ?? '—'}</Body>

        <Eyebrow>Impact Assessment</Eyebrow>
        <Body>{draft.dagrun_pm_impactassessment ?? '—'}</Body>

        <Eyebrow>Submitted</Eyebrow>
        <Body>{(draft.dagrun_pm_submittedbyname ?? draft.dagrun_pm_submittedby ?? '—')} · {draft.dagrun_pm_submittedon ? new Date(draft.dagrun_pm_submittedon).toLocaleString() : '—'}</Body>

        {(draft.dagrun_pm_reviewedon || draft.dagrun_pm_reviewedby) && (
          <>
            <Eyebrow>Reviewed</Eyebrow>
            <Body>{(draft.dagrun_pm_reviewedbyname ?? draft.dagrun_pm_reviewedby ?? '—')} · {draft.dagrun_pm_reviewedon ? new Date(draft.dagrun_pm_reviewedon).toLocaleString() : '—'}</Body>
          </>
        )}

        {(isFinal && draft.dagrun_pm_approvalnotes) && (
          <>
            <Eyebrow>Approval Notes</Eyebrow>
            <Body>{draft.dagrun_pm_approvalnotes}</Body>
          </>
        )}

        {isPM && !isFinal && (
          <>
            <Eyebrow>PM Review</Eyebrow>
            <div style={{ marginBottom: 12 }}>
              <label style={BM_LABEL}>Approval Notes</label>
              <textarea value={approvalNotes} onChange={e => setApprovalNotes(e.target.value)} style={BM_TEXTAREA} placeholder="Optional — rationale for approval / rejection." />
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <BMSuccessButton onClick={() => decide(ChangeRequestStatus.Approved, 'approve')} disabled={acting !== null}>
                {acting === 'approve' ? 'Approving…' : 'Approve'}
              </BMSuccessButton>
              <BMDangerButton onClick={() => decide(ChangeRequestStatus.Rejected, 'reject')} disabled={acting !== null}>
                {acting === 'reject' ? 'Rejecting…' : 'Reject'}
              </BMDangerButton>
              <BMCancelButton onClick={() => decide(ChangeRequestStatus.Withdrawn, 'withdraw')} disabled={acting !== null}>
                {acting === 'withdraw' ? 'Withdrawing…' : 'Withdraw'}
              </BMCancelButton>
            </div>
          </>
        )}
      </div>
    </>
  );
}

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: 11, fontWeight: 600, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 16, marginBottom: 4, paddingTop: 12, borderTop: '0.5px solid #EAECF0' }}>{children}</div>
);
const Body = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: 14, color: '#0F1117', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{children}</div>
);
